import { sedrApi, type SedrPhase, type SedrRequirement } from '../../api/sedr';
import { applyI18n, t } from '../../i18n';
import { bindLocaleRefresh } from '../../i18n/bindLocaleRefresh';
import { dialogHelpTopic } from '../../help/dialogHelpTopics';
import { bindModalDismiss, createModalShell } from '../../ui/modalDialogShell';
import { sedrErrorMessageKey } from './sedrErrors';
import { phaseOptionHtml } from './sedrPhases';
import { loadSedrCategories, renderCategorySelectOptions } from './sedrUi';
import {
  approvalValidityFieldHtml,
  bindApprovalGraceVisibility,
  parseApprovalValidityFromForm,
} from './sedrApprovalValidity';

export function showSedrEditRequirementDialog(options: {
  roomId: string;
  requirement: SedrRequirement;
  phases: SedrPhase[];
  onUpdated: () => void | Promise<void>;
}): void {
  const { requirement } = options;
  const shell = createModalShell({
    titleKey: 'sedr.requirement.editTitle',
    titleId: 'sedr-edit-req-title',
    helpTopic: dialogHelpTopic('sedrCreateRequirement'),
  });

  shell.body.innerHTML = `
    <form class="form-grid" id="sedr-edit-req-form">
      <label class="sedr-requirement-panel__span-2">
        <span data-i18n="sedr.table.title"></span>
        <input type="text" name="title" required maxlength="500" value="" autocomplete="off" />
      </label>
      <label class="sedr-requirement-panel__span-2">
        <span data-i18n="sedr.table.description"></span>
        <textarea name="description" maxlength="5000" rows="4"></textarea>
      </label>
      <label>
        <span data-i18n="sedr.requirement.category"></span>
        <select name="category"></select>
      </label>
      <label>
        <span data-i18n="sedr.table.owner"></span>
        <input type="text" name="ownerName" maxlength="200" autocomplete="off" />
      </label>
      <label>
        <span data-i18n="sedr.phases.field"></span>
        <select name="phaseId">
          ${phaseOptionHtml(options.phases, requirement.phaseId ?? null)}
        </select>
      </label>
      ${approvalValidityFieldHtml({
        amount: requirement.approvalValidityAmount ?? requirement.approvalValidityDays,
        unit: requirement.approvalValidityUnit,
        graceDays: requirement.approvalGraceDays,
      })}
      <p class="error-text" id="sedr-edit-req-error" hidden></p>
    </form>
  `;

  const form = shell.body.querySelector('#sedr-edit-req-form') as HTMLFormElement;
  const titleInput = form.querySelector('input[name="title"]') as HTMLInputElement;
  const descriptionInput = form.querySelector('textarea[name="description"]') as HTMLTextAreaElement;
  const ownerInput = form.querySelector('input[name="ownerName"]') as HTMLInputElement;
  const categorySelect = form.querySelector('select[name="category"]') as HTMLSelectElement;
  titleInput.value = requirement.title;
  descriptionInput.value = requirement.description ?? '';
  ownerInput.value = requirement.ownerName ?? '';

  void loadSedrCategories(options.roomId).then((categories) => {
    renderCategorySelectOptions(categorySelect, categories, requirement.category);
  });

  const cancelButton = document.createElement('button');
  cancelButton.type = 'button';
  cancelButton.className = 'button ghost';
  cancelButton.id = 'sedr-edit-req-cancel';
  cancelButton.setAttribute('data-i18n', 'common.cancel');

  const submitButton = document.createElement('button');
  submitButton.type = 'submit';
  submitButton.className = 'button primary';
  submitButton.id = 'sedr-edit-req-submit';
  submitButton.setAttribute('form', 'sedr-edit-req-form');
  submitButton.setAttribute('data-i18n', 'sedr.drawer.save');

  shell.actions.append(cancelButton, submitButton);
  document.body.append(shell.backdrop);
  applyI18n(shell.dialog);
  bindApprovalGraceVisibility(shell.body);

  const errorEl = shell.body.querySelector('#sedr-edit-req-error') as HTMLParagraphElement;
  let submitting = false;
  let lastErrorKey = 'status.error';

  const refreshLocale = () => {
    if (!errorEl.hidden) {
      errorEl.textContent = t(lastErrorKey);
    }
    applyI18n(shell.dialog);
  };
  const unbindLocale = bindLocaleRefresh(refreshLocale);

  const close = () => {
    if (submitting) return;
    unbindLocale();
    unbindDismiss();
    shell.backdrop.remove();
  };

  const unbindDismiss = bindModalDismiss({
    backdrop: shell.backdrop,
    closeButton: shell.closeButton,
    onClose: close,
  });

  cancelButton.addEventListener('click', close);
  titleInput.focus();

  form.addEventListener('submit', (event) => {
    event.preventDefault();
    if (submitting) return;

    void (async () => {
      submitting = true;
      errorEl.hidden = true;
      submitButton.disabled = true;
      const data = new FormData(form);
      try {
        const phaseId = String(data.get('phaseId') ?? '').trim() || '';
        const category = String(data.get('category') ?? '').trim();
        const validity = parseApprovalValidityFromForm(data);
        await sedrApi.patchRequirement(options.roomId, requirement.id, {
          title: String(data.get('title') ?? '').trim(),
          description: String(data.get('description') ?? '').trim(),
          ownerName: String(data.get('ownerName') ?? '').trim(),
          phaseId,
          category: category || undefined,
          approvalValidityAmount: validity.approvalValidityAmount,
          approvalValidityUnit: validity.approvalValidityUnit,
          clearApprovalValidity: validity.clearApprovalValidity,
          approvalGraceDays: validity.approvalGraceDays,
          clearApprovalGraceDays: validity.clearApprovalGraceDays,
        });
        submitting = false;
        unbindLocale();
        unbindDismiss();
        shell.backdrop.remove();
        await options.onUpdated();
      } catch (cause) {
        submitting = false;
        submitButton.disabled = false;
        lastErrorKey = sedrErrorMessageKey(cause);
        errorEl.hidden = false;
        errorEl.textContent = t(lastErrorKey);
      }
    })();
  });
}
